// @flow
import React, { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@apollo/client';
import Error from '@justpark/ui/src/components/Error';
import getGraphQlErrorsByPath from '@justpark/helpers/src/getGraphQlErrorsByPath/getGraphQlErrorsByPath';
import { useSelector } from 'react-redux';
import { selectIsAdminOrIsSuperuser, selectUserId } from '../stores/auth';
import ListingDetails from '../components/ListingDetails/ListingDetails';
import { gtagTrack, page } from '../helpers/Tracking/Analytics';
import GET_LISTING_DETAILS_QUERY from '../queries/listingDetails/getListingDetailsQuery.graphql';
import NotFound from '../components/Common/NotFound';
import LoadingState from '../components/ListingDetails/LoadingState';
import mapSizes from '../helpers/listingDetailsMapSizes';

import styles from '../../static/scss/pages/listingDetails.module.scss';

const richResultsData = (listing) => {
  const reviews = listing.reviews?.nodes || [];

  const data = {
    '@context': 'https://schema.org/',
    '@type': 'LocalBusiness',
    brand: {
      '@type': 'Brand',
      name: 'JustPark'
    },
    keywords:
      'parking spaces for rent, car parking, london parking, rent your driveway, let out your space, garages for rent, stadium parking, airport parking, garage rentals, parking, garages, driveways, rent, let',
    address: {
      '@type': 'PostalAddress',
      streetAddress: listing.address.line1,
      addressLocality: listing.address.city,
      postalCode: listing.address.postcode,
      addressCountry: listing.address.country
    },
    url: listing.canonicalUrl,
    description: listing.description,
    image: listing?.photos?.[0]?.normal.url || '',
    geo: {
      '@type': 'GeoCoordinates',
      latitude: listing.address?.lat,
      longitude: listing.address?.lng
    },
    name: listing.title,
    review: reviews.map((review) => ({
      '@type': 'Review',
      reviewRating: {
        '@type': 'Rating',
        ratingValue: review.rating,
        bestRating: '5'
      },
      author: {
        '@type': 'Person',
        name:
          review.sender.trim().length > 0 ? review.sender : 'Anonymous Driver'
      }
    }))
  };

  if (listing.feedback?.count > 0) {
    data.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: listing.feedback?.averageRating,
      ratingCount: listing.feedback?.count
    };
  }

  return data;
};

const useBreadcrumbsData = (breadcrumbs) => {
  const data = useMemo(() => {
    if (!breadcrumbs || breadcrumbs.length === 0) {
      return '';
    }

    return JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map(({ text, url }, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: text,
        item: url
      }))
    });
  }, [breadcrumbs]);

  return data;
};

const useShould404DueToCleansingState = (data) => {
  const isAdminOrIsSuperuser = useSelector(selectIsAdminOrIsSuperuser);

  const userId = useSelector(selectUserId);
  const spaceOwnerId = data?.listing?.userId;

  const isUserTheSpaceOwner =
    spaceOwnerId !== undefined && spaceOwnerId === userId;

  const isCleansingStateNotApproved =
    data && data.listing.cleansingState !== 'approved';

  return (
    isCleansingStateNotApproved &&
    !(isUserTheSpaceOwner || isAdminOrIsSuperuser)
  );
};

const ListingDetailsPage = () => {
  useEffect(() => {
    page('listing-details');
    gtagTrack('listing_landing_impression');
  }, []);

  const { slug } = useParams();

  const { loading, error, data, fetchMore } = useQuery(
    GET_LISTING_DETAILS_QUERY,
    {
      variables: {
        slug,
        reviewsOffset: 0,
        mapSizes
      },
      // needed to allow loading state to be set when doing fetchMore
      notifyOnNetworkStatusChange: true
    }
  );

  const listingErrors = getGraphQlErrorsByPath(error, 'listing');
  const listingIdErrors = getGraphQlErrorsByPath(error, 'listing.id');

  const didListingIdLookup422 = listingIdErrors[0]?.extensions.status === 422;

  const listingNotFound = listingErrors.find(
    (listingError) => listingError?.extensions?.status === 404
  );
  const listing = data?.listing || {};

  const should404DueToCleansingState = useShould404DueToCleansingState(data);

  // The BE endpoint will return a 422 on listing details when the listing model is not found
  // So we also need to check for that 422 as well as the 404 on slug lookup in order to work out if we are g2g
  const is404 =
    didListingIdLookup422 || listingNotFound || should404DueToCleansingState;

  // (loading && data) needed so that we don't reload ListingDetails component when doing fetchMore
  const isOk = !is404 && ((loading === false && !error) || (loading && data));

  const shouldShowLoading = loading && !data;

  const meta = listing?.meta || [];

  const breadCrumbData = useBreadcrumbsData(listing.breadcrumbs);

  const lcpImage = listing.photos?.[0]?.normal.url || null;

  return (
    <>
      <Helmet title={listing.pageTitle} titleTemplate="%s">
        {meta.map(({ key, value, type }) => {
          if (type === 'property') {
            return <meta key={key} property={key} content={value} />;
          }

          return <meta key={key} name={key} content={value} />;
        })}
        {isOk && <link rel="canonical" href={listing.canonicalUrl} />}

        {isOk && (
          <script type="application/ld+json">
            {JSON.stringify(richResultsData(listing))}
          </script>
        )}

        {isOk && (
          <script
            key="breadcrumbs"
            type="application/ld+json"
            data-cy="breadcrumbs-structured-data"
          >
            {breadCrumbData}
          </script>
        )}

        {lcpImage && <link rel="preload" as="image" href={lcpImage} />}
      </Helmet>
      <div className={styles.wrapper}>
        {is404 && <NotFound />}
        {shouldShowLoading && <LoadingState />}
        {error && <Error error={error.toString()} />}
        {isOk && (
          <ListingDetails
            location={listing}
            fetchMore={fetchMore}
            loading={loading}
          />
        )}
      </div>
    </>
  );
};

export default ListingDetailsPage;
